import * as React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface JourneyStep {
  label: React.ReactNode;
  sub?: React.ReactNode;
  state: 'done' | 'current' | 'upcoming';
}

export interface JourneyStepperProps extends React.HTMLAttributes<HTMLDivElement> {
  steps: JourneyStep[];
}

/**
 * JourneyStepper — Month-One Journey horizontal stepper.
 * Used at the top of the Command Centre to show the Builder's marker
 * across The Shift → The Game Plan → Acquisition OS build → First working version.
 */
export const JourneyStepper = React.forwardRef<HTMLDivElement, JourneyStepperProps>(
  ({ steps, className, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn('relative grid gap-0', className)}
      style={{ gridTemplateColumns: `repeat(${steps.length}, 1fr)` }}
      {...rest}
    >
      {/* connecting line */}
      <span
        aria-hidden
        className="absolute left-4 right-4 top-3.5 h-px bg-fd-border"
      />
      {steps.map((step, i) => (
        <StepNode key={i} step={step} index={i + 1} />
      ))}
    </div>
  ),
);
JourneyStepper.displayName = 'JourneyStepper';

function StepNode({ step, index }: { step: JourneyStep; index: number }) {
  const node = {
    done: 'bg-fd-accent text-fd-text border-fd-text',
    current:
      'bg-fd-accent text-fd-text border-fd-text shadow-[0_0_0_4px_var(--fd-accent-soft)]',
    upcoming: 'bg-fd-surface-2 text-fd-text-muted border-fd-border-strong',
  }[step.state];

  return (
    <div className="relative pr-6">
      <div
        className={cn(
          'relative z-10 grid h-7 w-7 place-items-center rounded-full border',
          'font-geist-mono text-[11px] font-medium',
          node,
        )}
      >
        {step.state === 'done' ? <Check className="h-3.5 w-3.5" /> : index}
      </div>
      <div className="mt-3">
        <div className="font-geist text-[13px] font-medium text-fd-text">{step.label}</div>
        {step.sub && (
          <div className="mt-0.5 font-geist-mono text-[10.5px] uppercase tracking-[0.04em] text-fd-text-subtle">
            {step.sub}
          </div>
        )}
      </div>
    </div>
  );
}
